import React, { useState, useEffect, useRef, useCallback } from "react";
import QuestionPopup from "./QuestionPOPup";
import axiosInstance from "../../../../../interceptor/axiosInstance";
import { useLocation, useNavigate } from "react-router-dom";
import BackButton from "../../../../BackButton";
import AddOneQuestion from "./AddOneQuestion";
import { toast } from '../toastUtils';
import "../../../../../Styles/MCQAssessments/QuestionTable.css";

// Skeleton Loader Component
const SkeletonRow = () => (
  <tr className="question-skeleton-row">
    <td><div className="question-skeleton question-skeleton-text"></div></td>
    <td><div className="question-skeleton question-skeleton-badge"></div></td>
    <td><div className="question-skeleton question-skeleton-badge"></div></td>
    <td>
      <div className="question-skeleton-actions">
        <div className="question-skeleton question-skeleton-btn"></div>
        <div className="question-skeleton question-skeleton-btn"></div>
        <div className="question-skeleton question-skeleton-btn"></div>
      </div>
    </td>
  </tr>
);

// Empty State Component
const EmptyState = ({ searchQuery, onClearSearch }) => {
  const hasSearch = searchQuery && searchQuery.trim().length > 0;
  
  return (
    <div className="question-empty-state">
      <div className="question-empty-icon">
        {hasSearch ? '🔍' : '📝'}
      </div>
      <h3 className="question-empty-title">
        {hasSearch ? 'No Questions Found' : 'No Questions Yet'}
      </h3>
      <p className="question-empty-message">
        {hasSearch ? (
          <>
            No questions match <strong>"{searchQuery}"</strong>
            <br />
            Try adjusting your search terms.
          </>
        ) : (
          'Start by adding your first question to this assessment.'
        )}
      </p>
      {hasSearch && (
        <button className="question-clear-search-btn" onClick={onClearSearch}>
          Clear Search
        </button>
      )}
    </div>
  );
};

const QuestionTable = () => {
  const location = useLocation();
  const assessment = location.state?.questions;
  const initialQuestions = location.state?.questions?.data || { results: [], pagination: {} };

  const [questionsData, setQuestionsData] = useState(initialQuestions.results);
  const [showPopup, setShowPopup] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [nextPage, setNextPage] = useState(initialQuestions.pagination?.next);
  const [prevPage, setPrevPage] = useState(initialQuestions.pagination?.previous);
  const [loading, setLoading] = useState(false);
  const [showAddPopup, setShowAddPopup] = useState(false);
  const [deletingQuestionId, setDeletingQuestionId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [jumpToPage, setJumpToPage] = useState('');
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  
  const navigate = useNavigate();
  const abortControllerRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  
  // Debounce search query
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    setSearching(true);
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      setSearching(false);
    }, 500);
    
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  const handleNavigate = useCallback(async (page = 1, size = pageSize, search = debouncedSearchQuery) => {
    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    abortControllerRef.current = new AbortController();
    setLoading(true);
    
    try {
      const response = await axiosInstance.get(
        `assessments/questions/`, {
          params: {
            test_id: assessment,
            page: page,
            page_size: size,
            name: search,
          },
          signal: abortControllerRef.current.signal,
        }
      );
      
      const results = response.data.data.results || [];
      const pagination = response.data.data.pagination || {};
      
      setQuestionsData(results);
      setNextPage(pagination.next);
      setPrevPage(pagination.previous);
      setPageNumber(page);
      setTotalCount(pagination.count || 0);
      setTotalPages(Math.ceil((pagination.count || 0) / size));
    } catch (error) {
      if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
        toast.error('Failed to load questions. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [assessment, pageSize, debouncedSearchQuery]);

  // Initial fetch and page size change
  useEffect(() => {
    handleNavigate(pageNumber, pageSize);
  }, [pageSize]);

  // Search effect
  useEffect(() => {
    if (debouncedSearchQuery !== undefined) {
      handleNavigate(1, pageSize, debouncedSearchQuery);
    }
  }, [debouncedSearchQuery]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleFirstPage = () => {
    if (pageNumber !== 1) handleNavigate(1, pageSize);
  };
  
  const handlePrevPage = () => {
    if (prevPage) handleNavigate(pageNumber - 1, pageSize);
  };

  const handleNextPage = () => {
    if (nextPage) handleNavigate(pageNumber + 1, pageSize);
  };
  
  const handleLastPage = () => {
    if (pageNumber !== totalPages && totalPages > 0) {
      handleNavigate(totalPages, pageSize);
    }
  };
  
  const handleJumpToPage = () => {
    const page = parseInt(jumpToPage, 10);
    if (page >= 1 && page <= totalPages) {
      handleNavigate(page, pageSize);
      setJumpToPage('');
    } else {
      toast.error(`Please enter a page number between 1 and ${totalPages}`);
    }
  };

  const handlePageSizeChange = (event) => {
    const newSize = parseInt(event.target.value, 10);
    setPageSize(newSize);
    setPageNumber(1);
    handleNavigate(1, newSize);
  };

  const fetchQuestionDetails = async (questionID) => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/assessments/questions/${questionID}/`);
      setSelectedQuestion(response.data);
      setShowPopup(true);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleNavigateOption = (questionID) => {
    navigate("/optionstable", { state: { questionID } });
  };

  const toggleEditPopup = () => {
    setShowPopup(!showPopup);
    setSelectedQuestion(null);
  };

  const toggleAddPopup = () => {
    setShowAddPopup(!showAddPopup);
  };

  const handleDeleteQuestion = async (questionId) => {
    if (deletingQuestionId) return; // Prevent multiple deletes
    
    if (!window.confirm('Are you sure you want to delete this question? This action cannot be undone.')) {
      return;
    }
    
    setDeletingQuestionId(questionId);
    
    try {
      await axiosInstance.delete(`/assessments/questions/${questionId}/`);
      toast.success('Question deleted successfully!');
      handleNavigate(pageNumber, pageSize);
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to delete question. Please try again.';
      toast.error(errorMessage);
    } finally {
      setDeletingQuestionId(null);
    }
  };
  
  const handleClearSearch = () => {
    setSearchQuery('');
    setDebouncedSearchQuery('');
    setPageNumber(1);
  };

  return (
    <div className="question-table-container">
      {/* Header */}
      <div className="question-table-header">
        <div className="question-table-header-left">
          <BackButton />
          <h1 className="question-table-title">Question Management</h1>
          {totalCount > 0 && (
            <span className="question-count-badge">{totalCount} Questions</span>
          )}
        </div>
        <div className="question-table-header-right">
          <button className="question-add-btn" onClick={toggleAddPopup}>
            <span className="question-add-icon">+</span>
            Add Question
          </button>
        </div>
      </div>
      
      {/* Search and Filters */}
      <div className="question-table-controls">
        <div className="question-search-wrapper">
          <input
            type="text"
            className="question-search-input"
            placeholder="Search questions by text..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searching && (
            <span className="question-search-spinner"></span>
          )}
          {searchQuery && (
            <button className="question-search-clear" onClick={handleClearSearch}>
              ✕
            </button>
          )}
        </div>
        
        <div className="question-page-size-wrapper">
          <label className="question-page-size-label">Show:</label>
          <select
            value={pageSize}
            onChange={handlePageSizeChange}
            className="question-page-size-select"
            disabled={loading}
          >
            <option value={15}>15</option>
            <option value={25}>25</option>
            <option value={30}>30</option>
          </select>
        </div>
      </div>
      {/* Table */}
      <div className="question-table-wrapper">
        <table className="question-table">
          <thead>
            <tr>
              <th className="question-th">Question Text</th>
              <th className="question-th question-th-level">Level</th>
              <th className="question-th question-th-type">Type</th>
              <th className="question-th question-th-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: pageSize }).map((_, idx) => (
                <SkeletonRow key={idx} />
              ))
            ) : questionsData.length > 0 ? (
              questionsData.map((question, index) => (
                <tr key={question.id || index} className="question-table-row">
                  <td className="question-td question-td-text">
                    <div className="question-text-content">
                      {question.text
                        .replaceAll("\r", "")
                        .split("\n")
                        .slice(0, 3)
                        .map((line, i) => (
                          <React.Fragment key={i}>
                            <span>{line}</span>
                            {i < 2 && <br />}
                          </React.Fragment>
                        ))}
                      {question.text.split("\n").length > 3 && (
                        <span className="question-text-more">...</span>
                      )}
                    </div>
                  </td>
                  <td className="question-td">
                    <span className={`question-level-badge question-level-${question.level?.toLowerCase()}`}>
                      {question.level}
                    </span>
                  </td>
                  <td className="question-td">
                    <span className="question-type-badge">
                      {question.question_type?.toUpperCase()}
                    </span>
                  </td>
                  <td className="question-td question-td-actions">
                    <div className="question-action-buttons-wrapper">
                      <button
                        className="question-action-btn question-edit-btn"
                        onClick={() => fetchQuestionDetails(question.id)}
                        title="Edit Question"
                      >
                        ✏️ Edit
                      </button>
                      <button
                        className={`question-action-btn question-options-btn ${
                          question.question_type?.toLowerCase() === "subjective"
                            ? "question-action-btn-disabled"
                            : ""
                        }`}
                        onClick={() => handleNavigateOption(question.id)}
                        disabled={question.question_type?.toLowerCase() === "subjective"}
                        title={question.question_type?.toLowerCase() === "subjective" ? "Not available for subjective questions" : "Manage Options"}
                      >
                        📝 Options
                      </button>
                      <button
                        className="question-action-btn question-delete-btn"
                        onClick={() => handleDeleteQuestion(question.id)}
                        disabled={deletingQuestionId === question.id}
                        title="Delete Question"
                      >
                        {deletingQuestionId === question.id ? (
                          <>
                            <span className="question-btn-spinner"></span>
                            Deleting...
                          </>
                        ) : (
                          <>🗑️ Delete</>
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" className="question-empty-cell">
                  <EmptyState searchQuery={searchQuery} onClearSearch={handleClearSearch} />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {questionsData.length > 0 && (
        <div className="question-pagination">
          <div className="question-pagination-info">
            Showing {((pageNumber - 1) * pageSize) + 1} to {Math.min(pageNumber * pageSize, totalCount)} of {totalCount} questions
          </div>
          
          <div className="question-pagination-controls">
            <button
              className="question-pagination-btn"
              onClick={handleFirstPage}
              disabled={!prevPage || loading}
              title="First page"
            >
              «
            </button>
            <button
              className="question-pagination-btn"
              onClick={handlePrevPage}
              disabled={!prevPage || loading}
              title="Previous page"
            >
              ‹ Prev
            </button>
            
            <div className="question-pagination-pages">
              <span className="question-pagination-current">Page {pageNumber} of {totalPages}</span>
            </div>
            
            <button
              className="question-pagination-btn"
              onClick={handleNextPage}
              disabled={!nextPage || loading}
              title="Next page"
            >
              Next ›
            </button>
            <button
              className="question-pagination-btn"
              onClick={handleLastPage}
              disabled={!nextPage || loading}
              title="Last page"
            >
              »
            </button>
          </div>
          
          <div className="question-pagination-jump">
            <span className="question-pagination-jump-label">Go to:</span>
            <input
              type="number"
              className="question-pagination-jump-input"
              value={jumpToPage}
              onChange={(e) => setJumpToPage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleJumpToPage()}
              placeholder="#"
              min="1"
              max={totalPages}
              disabled={loading}
            />
            <button
              className="question-pagination-jump-btn"
              onClick={handleJumpToPage}
              disabled={!jumpToPage || loading}
            >
              Go
            </button>
          </div>
        </div>
      )}

      {showPopup && (
        <QuestionPopup
          onClose={toggleEditPopup}
          questionData={selectedQuestion}
          loading={loading}
          refreshData={() => handleNavigate(pageNumber, pageSize)} // ✅ Refresh after update
        />
      )}

{showAddPopup && (
  <AddOneQuestion
    onClose={toggleAddPopup}
    assessmentId={assessment}
    refreshData={() => handleNavigate(pageNumber, pageSize)} // ✅ Re-fetch after add
  />
)}
    </div>
  );
};

export default QuestionTable;
